const itemIdentityKeys = ["id", "documentId", "userId", "categoryId", "memberId"] as const;

export function proposalCollectionItemHasIdentity(value: unknown, itemId: string): boolean {
  const canonicalItemId = normalizeIdentity(itemId);
  if (!canonicalItemId) return false;
  if (typeof value === "string") return normalizeIdentity(value) === canonicalItemId;
  if (!isRecord(value)) return false;

  const userId = normalizeIdentity(value.userId);
  const legalEntityId = normalizeIdentity(value.legalEntityId);
  if (userId && legalEntityId && `${userId}:${legalEntityId}` === canonicalItemId) return true;
  if (isRecord(value.documentInfo) && normalizeIdentity(value.documentInfo.id) === canonicalItemId) return true;
  return itemIdentityKeys.some((key) => normalizeIdentity(value[key]) === canonicalItemId);
}

export function getProposalCollectionItemIdentity(value: unknown): string | undefined {
  if (typeof value === "string") return normalizeIdentity(value);
  if (!isRecord(value)) return undefined;

  const userId = normalizeIdentity(value.userId);
  const legalEntityId = normalizeIdentity(value.legalEntityId);
  if (userId && legalEntityId) return `${userId}:${legalEntityId}`;
  if (isRecord(value.documentInfo)) {
    const documentInfoId = normalizeIdentity(value.documentInfo.id);
    if (documentInfoId) return documentInfoId;
  }
  return itemIdentityKeys.map((key) => normalizeIdentity(value[key])).find(Boolean);
}

/**
 * The identity of the collection item this one REPLACES, if it declares one.
 *
 * A document replacement carries two identities: its own, and — through `replacesId` — the document it
 * stands in for. Anything reconciling a collection by identity has to know that lineage, or the two
 * halves of one replace read as an unrelated add and an unrelated removal. `FieldRedlineArray.create`
 * has always matched entries this way; this is the same rule for the canonical projection, whose items
 * carry `replacesId` on the wrapper (and, for a locally-projected add, inside `documentInfo` too).
 */
export function getProposalCollectionItemReplacedIdentity(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;
  return (
    normalizeIdentity(value.replacesId) ??
    (isRecord(value.documentInfo) ? normalizeIdentity(value.documentInfo.replacesId) : undefined)
  );
}

/** Whether `value` is the item that replaced the collection item identified by `itemId`. */
export function proposalCollectionItemReplaces(value: unknown, itemId: string): boolean {
  const canonicalItemId = normalizeIdentity(itemId);
  if (!canonicalItemId) return false;
  return getProposalCollectionItemReplacedIdentity(value) === canonicalItemId;
}

function normalizeIdentity(value: unknown): string | undefined {
  if (typeof value === "string") return value.toLowerCase();
  if (isRecord(value) && typeof value.value === "string") return value.value.toLowerCase();
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
